import React, { useState } from 'react'

const ROLE_LABELS = {
  offensive: 'Ofensiva',
  defensive: 'Defensiva',
  utility: 'Utilidad',
  support: 'Soporte',
}

/** Fallback local si el API aún no manda displayTitle / displaySubtitle. */
const ACADEMY_TITLE_FALLBACK = {
  Bunshin: { title: 'Bunshin no Jutsu', subtitle: 'Técnica de Clones' },
  Henge: { title: 'Henge no Jutsu', subtitle: 'Técnica de Transformación' },
  Kawarimi: { title: 'Kawarimi no Jutsu', subtitle: 'Técnica de Sustitución' },
  Kakuremino: { title: 'Kakuremino no Jutsu', subtitle: 'Técnica del Manto Oculto' },
  Nawanuke: { title: 'Nawanuke no Jutsu', subtitle: 'Técnica de Escape de Ataduras' },
}

function resolveDisplayTitles(t = {}) {
  if (t.displayTitle) {
    return { title: t.displayTitle, subtitle: t.displaySubtitle || null }
  }
  const catalog = ACADEMY_TITLE_FALLBACK[t.name]
  if (catalog) return catalog

  const rawSub = String(t.subtitle || '').trim()
  if (rawSub) {
    const parts = rawSub.split(/\s*[—–]\s*/).map((s) => s.trim()).filter(Boolean)
    if (parts.length >= 2) {
      return { title: parts[0], subtitle: parts.slice(1).join(' — ') }
    }
    return { title: rawSub, subtitle: null }
  }
  return { title: t.name || 'Sin nombre', subtitle: null }
}

function reasonText(reason) {
  if (!reason) return null
  if (typeof reason === 'string') return reason
  if (reason.message) return reason.message
  return reason.code || null
}

function hasValue(v) {
  return v != null && String(v).trim() !== ''
}

/**
 * Página de grimorio — panel pergamino colapsable.
 * Espera el DTO de GET /personajes/:slug/techniques (campos planos).
 */
export default function CharacterTechniqueCard({
  technique: t,
  canLearn = false,
  learning = false,
  onLearn,
  cannotAfford = false,
  defaultExpanded = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const learned = Boolean(t?.learned)
  const isTraining = Boolean(t?.status === 'learning')
  const earned = Math.max(0, Number(t?.disciplineEarned) || 0)
  const required = Math.max(0, Number(t?.requiredDiscipline) || 0)
  const reasons = Array.isArray(t?.reasons) ? t.reasons : []
  const locked = !learned && !isTraining && reasons.length > 0
  const contentId = `char-tech-${t?.techniqueId || t?.name || 'x'}`
  const price = Number(t?.priceRyous) > 0 ? Number(t.priceRyous) : 0
  const progressPct =
    isTraining && required > 0 ? Math.min(100, Math.round((earned / required) * 100)) : 0

  const types = Array.isArray(t?.types) ? t.types : []
  const thresholds = Array.isArray(t?.thresholds) ? t.thresholds : []
  const rankDisplay = t?.rankLabel || t?.rank_label || t?.rank || null
  const role = t?.combatRole ? ROLE_LABELS[t.combatRole] || t.combatRole : null

  const seals = t?.seals ?? t?.seal_cost ?? t?.sealCost
  const movements = t?.movements ?? t?.movement_cost ?? t?.action_cost
  const gasto = t?.gasto ?? null
  const damage = t?.damage ?? t?.damageEffect ?? null
  const { title: displayTitle, subtitle: displaySubtitle } = resolveDisplayTitles(t)

  const statusSeal = learned
    ? { label: 'Aprendida', mod: 'learned' }
    : isTraining
      ? { label: 'En entrenamiento', mod: 'training' }
      : locked
        ? { label: 'Bloqueada', mod: 'locked' }
        : cannotAfford
          ? { label: 'Sin ryous', mod: 'locked' }
          : canLearn
            ? { label: 'Disponible', mod: 'ready' }
            : null

  const catalogBits = [role, ...types, t?.element || null].filter(Boolean)

  const stats = [
    { label: 'Sellos', value: hasValue(seals) ? String(seals) : null },
    { label: 'Movimientos', value: hasValue(movements) ? String(movements) : null },
    { label: 'Gasto', value: hasValue(gasto) ? String(gasto) : null },
    { label: 'Daño / efecto', value: hasValue(damage) ? String(damage) : null },
    { label: 'Distancia', value: hasValue(t?.distance) ? String(t.distance) : null },
    { label: 'Área', value: hasValue(t?.area) ? String(t.area) : null },
    { label: 'Duración', value: hasValue(t?.duration) ? String(t.duration) : null },
    {
      label: 'Rango mín.',
      value: hasValue(t?.effectiveRankMin || t?.rankMin || t?.prerequisites?.rankMin)
        ? String(t?.effectiveRankMin || t?.rankMin || t?.prerequisites?.rankMin)
        : null,
    },
    price > 0 && !learned ? { label: 'Precio', value: `${price} ryō` } : null,
    !learned && required > 0 ? { label: 'Disciplina', value: String(required) } : null,
  ].filter((s) => s && s.value)

  const previewStats = stats.slice(0, 3)

  const learnLabel = learning
    ? 'Aprendiendo…'
    : required > 0
      ? price > 0
        ? `Entrenar · ${price} ryō`
        : 'Entrenar'
      : price > 0
        ? `Aprender · ${price} ryō`
        : 'Aprender'

  const toggle = () => setExpanded((v) => !v)

  return (
    <article
      className={`char-tech-card${expanded ? ' is-expanded' : ''}${learned ? ' is-learned' : ''}${
        locked ? ' is-locked' : ''
      }${isTraining ? ' is-training' : ''}`}
    >
      <div className="char-tech-card__accent" aria-hidden="true" />

      <header className="char-tech-card__header">
        <div className="char-tech-card__heading">
          {statusSeal ? (
            <span className={`char-tech-seal char-tech-seal--${statusSeal.mod}`}>
              {statusSeal.label}
            </span>
          ) : null}
          <h4 className="char-tech-card__name">{displayTitle}</h4>
          {displaySubtitle ? <p className="char-tech-card__subtitle">{displaySubtitle}</p> : null}
          {(rankDisplay || catalogBits.length > 0) && (
            <p className="char-tech-card__meta">
              {[rankDisplay, ...catalogBits].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <div className="char-tech-card__toolbar">
          {!learned && !isTraining && canLearn ? (
            <button
              type="button"
              className="char-btn-accent char-tech-card__cta"
              disabled={learning}
              onClick={() => onLearn?.(t.techniqueId)}
            >
              {learnLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="char-tech-card__toggle"
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={toggle}
          >
            <span className="char-tech-card__toggle-label">
              {expanded ? 'Cerrar' : 'Abrir'}
            </span>
            <span className="char-tech-card__chevron" aria-hidden="true">
              {expanded ? '▴' : '▾'}
            </span>
          </button>
        </div>
      </header>

      {!expanded && previewStats.length > 0 ? (
        <dl className="char-tech-stats char-tech-stats--preview">
          {previewStats.map(({ label, value }) => (
            <div key={label} className="char-tech-stats__item">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {isTraining && required > 0 ? (
        <div className="char-tech-discipline" aria-label={`Disciplina ${earned} de ${required}`}>
          <div className="char-tech-discipline__row">
            <span className="char-tech-discipline__label">
              Disciplina {earned} / {required}
            </span>
            <span className="char-tech-discipline__pct">{progressPct}%</span>
          </div>
          <div className="char-tech-discipline__track" aria-hidden="true">
            <div className="char-tech-discipline__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="char-tech-discipline__hint">Uso en aventura: +1 · Misión: +5</p>
        </div>
      ) : null}

      <div id={contentId} className="char-tech-card__body" hidden={!expanded}>
        {reasons.length ? (
          <div className="char-tech-card__reasons">
            <p className="char-tech-card__kicker">Requisitos pendientes</p>
            <ul>
              {reasons.map((r, i) => {
                const text = reasonText(r)
                if (!text) return null
                return <li key={`${r?.code || 'r'}-${i}`}>{text}</li>
              })}
            </ul>
          </div>
        ) : null}

        <div className="char-tech-card__desc">
          <p className="char-tech-card__kicker">Descripción</p>
          <p>{t?.description || 'Sin descripción registrada.'}</p>
        </div>

        {stats.length ? (
          <div className="char-tech-card__stats-block">
            <p className="char-tech-card__kicker">Parámetros</p>
            <dl className="char-tech-stats">
              {stats.map(({ label, value }) => (
                <div key={label} className="char-tech-stats__item">
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {thresholds.length ? (
          <div className="char-tech-card__thresholds">
            <p className="char-tech-card__kicker">Umbrales</p>
            <ul>
              {thresholds.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </article>
  )
}
